import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { RotateLoader } from "react-spinners";
import { GrLinkNext, GrLinkPrevious } from "react-icons/gr";

import { AuthContext } from "../../context/main/auth-context";
import "../../css/orders/orders.css";

import OrdersHeader from "../../components/orders/orders-header";
import OrderListItemsManualOrderShipStep2 from "../../components/orders/order-list-item-manual-order-ship-step-2";
import AssistantToggleButton from "../../components/main/assistant-toggle-button";
import shipManualAddedOrderItem from "../../components/orders/ship-order-item";
import getMultipleShippingLabelsPdfPostNL from "../../components/bol/get-multiple-shipping-labels-pdf-postnl";

const OrdersManualAdded = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState("");
  const [loadingState, setLoadingState] = useState("");
  const [checkedOrders, setCheckedOrders] = useState([]); // start als array

  useEffect(() => {
    setLoadingState("Verzendoverzicht wordt geladen");
    try {
      const checked = JSON.parse(sessionStorage.getItem("checkedOrders") || "[]");
      setCheckedOrders(Array.isArray(checked) ? checked : []);
    } catch (e) {
      setCheckedOrders([]);
      setErrorMessage("Kon geselecteerde bestellingen niet laden.");
    } finally {
      setLoadingState("");
    }
  }, []);

  const returnToStep1Handler = () => {
    navigate("/orders/manual-added/ship/step-1");
    window.scrollTo(0, 0);
  };

  // transport-mapping voor bol
  const mapTransporterCodeForBol = (code) => {
    if (code === "POSTNL_2928_STEALTH") return "TNT_BRIEF"; // stealth => briefpost
    if (code === "TNT_BRIEF") return "TNT_BRIEF";
    // overige PostNL varianten voor bol => TNT
    if (code === "POSTNL_2928" || code === "POSTNL_3085") return "TNT";
    return code || "TNT";
  };

  const confirmToStep3Handler = async () => {
    setErrorMessage("");
    setLoadingState("Verzendlabels worden opgehaald...");

    try {
      // 1) Split orders: met label (niet-briefpost) vs briefpost
      const ordersNeedingLabels = checkedOrders.filter(
        (o) => mapTransporterCodeForBol(o.frontEndChosenDeliveryOption?.transporterCode) !== "TNT_BRIEF"
      );

      let labelsPdf = { ResponseShipments: [] };

      if (ordersNeedingLabels.length > 0) {
        const res = await getMultipleShippingLabelsPdfPostNL(ordersNeedingLabels, auth.token);

        const shipments = res?.ResponseShipments;
        const ok =
          !!res &&
          Array.isArray(shipments) &&
          shipments.length === ordersNeedingLabels.length;

        if (!ok) {
          setErrorMessage("Er is iets misgegaan bij het ophalen van verzendlabels. Probeer het opnieuw.");
          setLoadingState("");
          return;
        }

        labelsPdf = res;

        // 2) Koppel labels aan orders (volgorde 1-op-1)
        ordersNeedingLabels.forEach((order, idx) => {
          const label = shipments[idx];
          order.shipmentPostNL = label;
          // BOL verwacht TNT + Barcode
          order.frontEndChosenDeliveryOption.transporterCode = "TNT";
          order.frontEndChosenDeliveryOption.inputTrackAndTrace = label?.Barcode;
          order.inputTrackAndTrace = label?.Barcode;
        });
      }

      // 3) Voor briefpost (incl. stealth) mappen + T&T leeg
      checkedOrders.forEach((order) => {
        const mapped = mapTransporterCodeForBol(order.frontEndChosenDeliveryOption?.transporterCode);
        if (mapped === "TNT_BRIEF") {
          order.frontEndChosenDeliveryOption.transporterCode = "TNT_BRIEF";
          order.frontEndChosenDeliveryOption.inputTrackAndTrace = undefined;
          order.inputTrackAndTrace = undefined;
        }
      });

      // 4) Shippen naar bol per item
      let handled = 0;
      const total = checkedOrders.reduce(
        (sum, o) => sum + (o.frontEndChosenDeliveryOption?.orderItems?.length || 0),
        0
      );

      setLoadingState(`0/${total} bestelregels verwerkt...`);

      for (const order of checkedOrders) {
        const items = order.frontEndChosenDeliveryOption?.orderItems || [];
        for (const item of items) {
          item.shippedSuccesful = false;

          try {
            const res = await shipManualAddedOrderItem(auth, order, item);
            if (res?.ok && (res.status === 202 || res.status === 200)) {
              item.shippedSuccesful = true;
            }
          } catch (e) {
            // laat shippedSuccesful = false; we tonen dit in stap 3
          } finally {
            handled += 1;
            setLoadingState(`${handled}/${total} bestelregels verwerkt...`);
          }
        }
      }

      // 5) Opslaan & door
      sessionStorage.setItem("labelsPDF", JSON.stringify(labelsPdf));
      sessionStorage.setItem("checkedOrders", JSON.stringify(checkedOrders));
      navigate("/orders/manual-added/ship/step-3");
      window.scrollTo(0, 0);
    } catch (e) {
      setErrorMessage("Er is iets misgegaan tijdens het verwerken van de zendingen.");
    } finally {
      setLoadingState("");
    }
  };

  return (
    <React.Fragment>
      <div className="title-header title-header-ship-orders">
        <span>Verzenden van bestellingen</span>
        <div className="shipmentSteps">
          <div className="shipmentOneStep shipmentFirstStep finished">
            <div className="shipmentOneStepCircle">1</div>
            <div className="">Verzendmethode</div>
          </div>
          <div className="shipmentStepsLine"></div>
          <div className="shipmentOneStep finished">
            <div className="shipmentOneStepCircle">2</div>
            <div className="">Overzicht</div>
          </div>
          <div className="shipmentStepsLine"></div>
          <div className="shipmentOneStep notActive">
            <div className="shipmentOneStepCircle">3</div>
            <div className="">Bevestiging</div>
          </div>
        </div>
      </div>

      <OrdersHeader recountOrders={false} />

      {!!errorMessage && <div className="errorMessageField">{errorMessage}</div>}

      {checkedOrders.length > 0 && (
        <div className="orders-container-header ship-orders-container-header">
          <div className="orders-container-header-actions ship-orders-container-header-actions">
            Overzicht
          </div>
        </div>
      )}

      {checkedOrders.length > 0 &&
        checkedOrders.map((order) => (
          <OrderListItemsManualOrderShipStep2
            order={order}
            key={order.id || order.orderId || order._id}
          />
        ))}

      <div className="orders-container-footer-actions ship-orders-container-footer-actions step2">
        <Button className="previousBtn" onClick={returnToStep1Handler}>
          <GrLinkPrevious /> Vorige stap
        </Button>
        <Button
          className="nextBtn"
          onClick={confirmToStep3Handler}
          disabled={checkedOrders.length === 0}
        >
          Verwerken <GrLinkNext />
        </Button>
      </div>

      {loadingState && (
        <div className="loading-container">
          <div className="loading-container-title">{loadingState}</div>
          <RotateLoader
            size={100}
            width={100}
            height={10}
            color={"#fff"}
            loading={!!loadingState}
          />
        </div>
      )}

      <AssistantToggleButton />
    </React.Fragment>
  );
};

export default OrdersManualAdded;
